import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button, Card, Descriptions, Space, Spin, Tag } from 'antd';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import * as Component from '../../../../shared/components';
import { formatDateTimePSTWithSeconds } from '../../../../shared/utils/timeFormat';
import { mediaholdIngestService } from '../service/mediaholdIngestService';
import {
  MediaholdIngestHistoryDetail as MediaholdIngestHistoryDetailType,
  PROCESSED_STATUS_COLORS,
  PROVIDER_TYPE_COLORS,
} from '../type/mediaholdIngestType';

const MediaholdIngestDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<MediaholdIngestHistoryDetailType | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      loadData(parseInt(id, 10));
    }
  }, [id]);

  const loadData = async (ingestMessageId: number) => {
    try {
      setLoading(true);
      const result = await mediaholdIngestService.fetchOne(ingestMessageId);
      setData(result);
    } catch (error) {
      console.error('Failed to fetch Mediahold ingest detail:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Component.BasePageComponent>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      </Component.BasePageComponent>
    );
  }

  if (!data) {
    return (
      <Component.BasePageComponent>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <h2>Data not found.</h2>
          <Button type="primary" onClick={() => navigate('/mediahold-ingest')}>
            Back to List
          </Button>
        </div>
      </Component.BasePageComponent>
    );
  }

  return (
    <Component.BasePageComponent>
      <div style={{ padding: '24px' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/mediahold-ingest')}>
              Back to List
            </Button>
            <h2 style={{ margin: 0 }}>Mediahold Ingest History Detail - ID: {data.id}</h2>
            <div style={{ width: '80px' }} />
          </div>

          {/* Basic Information */}
          <Card title="Basic Information" bordered>
            <Descriptions column={2} bordered>
              <Descriptions.Item label="ID">{data.id}</Descriptions.Item>
              <Descriptions.Item label="Provider Type">
                <Tag color={PROVIDER_TYPE_COLORS[data.provider_type] || 'default'}>
                  {data.provider_type.toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Program Name" span={2}>
                {data.program_name}
              </Descriptions.Item>
              <Descriptions.Item label="Episode No">{data.episode_no}</Descriptions.Item>
              <Descriptions.Item label="Section No">{data.section_no || '-'}</Descriptions.Item>
              <Descriptions.Item label="On Air Date">{data.on_air_date}</Descriptions.Item>
              <Descriptions.Item label="Network">{data.network}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={PROCESSED_STATUS_COLORS[data.processed] || 'default'}>
                  {data.processed.toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Fail Count">
                <span style={{ color: data.fail_count > 0 ? '#ff4d4f' : 'inherit' }}>
                  {data.fail_count}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Created">
                {formatDateTimePSTWithSeconds(data.created)}
              </Descriptions.Item>
              <Descriptions.Item label="Modified">
                {formatDateTimePSTWithSeconds(data.modified)}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Season Information */}
          {data.season_id ? (
            <Card title="Season Information" bordered>
              <Descriptions column={2} bordered>
                <Descriptions.Item label="Season ID" span={data.season_title ? 1 : 2}>
                  <Link to={`/season/edit2/${data.season_id}`} style={{ color: '#1890ff' }}>
                    {data.season_id}
                  </Link>
                </Descriptions.Item>
                {data.season_title && (
                  <Descriptions.Item label="Slug">{data.season_title.slug}</Descriptions.Item>
                )}
                {data.season_title && (
                  <Descriptions.Item label="Title (KO)" span={2}>
                    {data.season_title.title}
                  </Descriptions.Item>
                )}
                {data.season_title && (
                  <Descriptions.Item label="Title (EN)" span={2}>
                    {data.season_title.title_en}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>
          ) : (
            <Card title="Season Information" bordered>
              <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                No season mapping information available.
              </div>
            </Card>
          )}

          {/* Content Information */}
          {data.content_id ? (
            <Card title="Content Information" bordered>
              <Descriptions column={1} bordered>
                <Descriptions.Item label="Content ID">
                  <Link to={`/episode/edit/${data.content_id}`} style={{ color: '#1890ff' }}>
                    {data.content_id}
                  </Link>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          ) : (
            <Card title="Content Information" bordered>
              <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                Content has not been created.
              </div>
            </Card>
          )}

          {/* Error Message */}
          {data.error_message && (
            <Card title="Error Message" bordered>
              <pre
                style={{
                  background: '#fff2f0',
                  border: '1px solid #ffccc7',
                  padding: '12px',
                  borderRadius: '4px',
                  color: '#ff4d4f',
                  maxHeight: '200px',
                  overflow: 'auto',
                }}
              >
                {data.error_message}
              </pre>
            </Card>
          )}

          {/* Subtitle URL */}
          {data.subtitle_url && (
            <Card title="Subtitle Information" bordered>
              <Descriptions column={1} bordered>
                <Descriptions.Item label="Subtitle URL">
                  <a href={data.subtitle_url} target="_blank" rel="noopener noreferrer">
                    {data.subtitle_url}
                  </a>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          )}

          {/* Raw Body JSON */}
          <Card title="Raw Body JSON" bordered>
            <pre
              style={{
                maxHeight: '400px',
                overflow: 'auto',
                background: '#f5f5f5',
                padding: '12px',
                borderRadius: '4px',
              }}
            >
              {JSON.stringify(data.raw_body_json, null, 2)}
            </pre>
          </Card>
        </Space>
      </div>
    </Component.BasePageComponent>
  );
};

export default MediaholdIngestDetail;

